// src/core/modals/peoples/supplierModal.jsx
import { React, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createSupplier, updateSupplier } from "../../../api/supplier";
import { uploadCatalogImage } from "../../../api/productApi"; // ✅ reuse same uploader
import { toast } from "react-toastify";
import PropTypes from "prop-types";

// --- helpers (same semantics as biller) ---------------------------------------
const normalizeKey = (s) => {
  if (!s) return "";
  let v = String(s).trim();
  v = v.replace(/^\/+/, "");
  v = v.replace(/^catelog\//i, "catalog/");
  if (!/^catalog\//i.test(v)) v = `catalog/${v.split("/").pop()}`;
  return v;
};
const toPublicUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  const key = normalizeKey(v);
  const base = (process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com").replace(/\/+$/, "");
  return `${base}/${key}`;
};
const buildSupplierFileName = (file, nameVal, emailVal) => {
  const ext =
    (file.type && file.type.split("/")[1]) ||
    file.name.split(".").pop() ||
    "jpg";
  const stamp = new Date().toISOString().replace(/[-:.TZ]/g, "").slice(0, 14);
  const base = (nameVal || emailVal || "supplier")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");
  // e.g., SUPPLIER_acme_20251112094500.jpg
  return `SUPPLIER_${base || "supplier"}_${stamp}.${ext}`;
};
// ------------------------------------------------------------------------------

const SupplierModal = ({ selectedSupplier, onDeleteSupplier }) => {
  // Add Supplier States
  const [supplierFirstNameEN, setSupplierFirstNameEN] = useState("");
  const [supplierFirstNameAR, setSupplierFirstNameAR] = useState("");
  const [supplierLastNameEN, setSupplierLastNameEN] = useState("");
  const [supplierLastNameAR, setSupplierLastNameAR] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");
  const [supplierPhone, setSupplierPhone] = useState("");
  const [supplierAddressEN, setSupplierAddressEN] = useState("");
  const [supplierAddressAR, setSupplierAddressAR] = useState("");
  const [supplierCityEN, setSupplierCityEN] = useState("");
  const [supplierCityAR, setSupplierCityAR] = useState("");
  const [supplierStateEN, setSupplierStateEN] = useState("");
  const [supplierStateAR, setSupplierStateAR] = useState("");
  const [supplierCountryEN, setSupplierCountryEN] = useState("");
  const [supplierCountryAR, setSupplierCountryAR] = useState("");
  const [supplierPostalCode, setSupplierPostalCode] = useState("");
  const [supplierStatus, setSupplierStatus] = useState(true);

  // ✅ image (Add): preview URL + S3 key
  const [supplierImagePreview, setSupplierImagePreview] = useState("");
  const [supplierImageKey, setSupplierImageKey] = useState("");

  // Edit Supplier States
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [editSupplierFirstNameEN, setEditSupplierFirstNameEN] = useState("");
  const [editSupplierFirstNameAR, setEditSupplierFirstNameAR] = useState("");
  const [editSupplierLastNameEN, setEditSupplierLastNameEN] = useState("");
  const [editSupplierLastNameAR, setEditSupplierLastNameAR] = useState("");
  const [editSupplierEmail, setEditSupplierEmail] = useState("");
  const [editSupplierPhone, setEditSupplierPhone] = useState("");
  const [editSupplierAddressEN, setEditSupplierAddressEN] = useState("");
  const [editSupplierAddressAR, setEditSupplierAddressAR] = useState("");
  const [editSupplierCityEN, setEditSupplierCityEN] = useState("");
  const [editSupplierCityAR, setEditSupplierCityAR] = useState("");
  const [editSupplierStateEN, setEditSupplierStateEN] = useState("");
  const [editSupplierStateAR, setEditSupplierStateAR] = useState("");
  const [editSupplierCountryEN, setEditSupplierCountryEN] = useState("");
  const [editSupplierCountryAR, setEditSupplierCountryAR] = useState("");
  const [editSupplierPostalCode, setEditSupplierPostalCode] = useState("");
  const [editSupplierStatus, setEditSupplierStatus] = useState(true);

  // ✅ image (Edit): preview URL + S3 key
  const [editSupplierImagePreview, setEditSupplierImagePreview] = useState("");
  const [editSupplierImageKey, setEditSupplierImageKey] = useState("");

  // populate edit when a row is chosen
  useEffect(() => {
    if (!selectedSupplier) return;
    setEditingSupplierId(selectedSupplier.id);
    setEditSupplierFirstNameEN(selectedSupplier.firstName?.en || "");
    setEditSupplierFirstNameAR(selectedSupplier.firstName?.ar || "");
    setEditSupplierLastNameEN(selectedSupplier.lastName?.en || "");
    setEditSupplierLastNameAR(selectedSupplier.lastName?.ar || "");
    setEditSupplierEmail(selectedSupplier.email || "");
    setEditSupplierPhone(selectedSupplier.phone || "");
    setEditSupplierAddressEN(selectedSupplier.address?.en || "");
    setEditSupplierAddressAR(selectedSupplier.address?.ar || "");
    setEditSupplierCityEN(selectedSupplier.city?.en || "");
    setEditSupplierCityAR(selectedSupplier.city?.ar || "");
    setEditSupplierStateEN(selectedSupplier.state?.en || "");
    setEditSupplierStateAR(selectedSupplier.state?.ar || "");
    setEditSupplierCountryEN(selectedSupplier.country?.en || "");
    setEditSupplierCountryAR(selectedSupplier.country?.ar || "");
    setEditSupplierPostalCode(selectedSupplier.postalCode || "");
    setEditSupplierStatus(
      selectedSupplier.status === "active" || selectedSupplier.status === "Active"
    );

    // existing image -> normalize to key + make public preview
    const existing = selectedSupplier.image || "";
    setEditSupplierImageKey(existing ? normalizeKey(existing) : "");
    setEditSupplierImagePreview(existing ? toPublicUrl(existing) : "");
  }, [selectedSupplier]);

  // ---------------------------------------------------------------------------
  // Image uploads
  // ---------------------------------------------------------------------------
  const handleAddImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const dataUrl = reader.result;
      setSupplierImagePreview(dataUrl); // instant temp preview

      try {
        const desired = buildSupplierFileName(file, supplierFirstNameEN, supplierEmail);
        const { key } = await uploadCatalogImage(dataUrl, desired);
        const k = normalizeKey(key);
        setSupplierImageKey(k);
        setSupplierImagePreview(toPublicUrl(k)); // swap to S3 public URL
      } catch (err) {
        console.error(err);
        toast.error(window.translate("Image upload failed"));
        setSupplierImagePreview("");
        setSupplierImageKey("");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleEditImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const dataUrl = reader.result;
      setEditSupplierImagePreview(dataUrl);

      try {
        const desired = buildSupplierFileName(file, editSupplierFirstNameEN, editSupplierEmail);
        const { key } = await uploadCatalogImage(dataUrl, desired);
        const k = normalizeKey(key);
        setEditSupplierImageKey(k);
        setEditSupplierImagePreview(toPublicUrl(k));
      } catch (err) {
        console.error(err);
        toast.error(window.translate("Image upload failed"));
      }
    };
    reader.readAsDataURL(file);
  };

  // ---------------------------------------------------------------------------
  // CRUD
  // ---------------------------------------------------------------------------
  const handleAddSupplier = async (e) => {
    e.preventDefault();

    if (
      !supplierFirstNameEN ||
      !supplierFirstNameAR ||
      !supplierLastNameEN ||
      !supplierLastNameAR ||
      !supplierEmail ||
      !supplierPhone
    ) {
      toast.error(`❌ ${window.translate("Please fill all required fields")}`);
      return;
    }

    const payload = {
      firstName: { en: supplierFirstNameEN, ar: supplierFirstNameAR },
      lastName: { en: supplierLastNameEN, ar: supplierLastNameAR },
      email: supplierEmail,
      phone: supplierPhone,
      address: { en: supplierAddressEN, ar: supplierAddressAR },
      city: { en: supplierCityEN, ar: supplierCityAR },
      state: { en: supplierStateEN, ar: supplierStateAR },
      country: { en: supplierCountryEN, ar: supplierCountryAR },
      postalCode: supplierPostalCode,
      image: supplierImageKey || "", // ✅ send S3 key only
      status: supplierStatus ? "active" : "inactive",
    };

    try {
      await createSupplier(payload);
      toast.success(` ${window.translate("Supplier created successfully")}`);
      // reset image controls
      setSupplierImagePreview("");
      setSupplierImageKey("");
      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      toast.error(`❌ ${window.translate("Failed to create supplier")}`);
    }
  };

  const handleEditSupplier = async (e) => {
    e.preventDefault();

    if (
      !editSupplierFirstNameEN ||
      !editSupplierFirstNameAR ||
      !editSupplierLastNameEN ||
      !editSupplierLastNameAR ||
      !editSupplierEmail ||
      !editSupplierPhone
    ) {
      toast.error(`❌ ${window.translate("Please fill all required fields")}`);
      return;
    }

    const payload = {
      firstName: { en: editSupplierFirstNameEN, ar: editSupplierFirstNameAR },
      lastName: { en: editSupplierLastNameEN, ar: editSupplierLastNameAR },
      email: editSupplierEmail,
      phone: editSupplierPhone,
      address: { en: editSupplierAddressEN, ar: editSupplierAddressAR },
      city: { en: editSupplierCityEN, ar: editSupplierCityAR },
      state: { en: editSupplierStateEN, ar: editSupplierStateAR },
      country: { en: editSupplierCountryEN, ar: editSupplierCountryAR },
      postalCode: editSupplierPostalCode,
      // ✅ if user uploaded new image, use that key; else keep existing (derive from preview)
      image: editSupplierImageKey || normalizeKey(editSupplierImagePreview || ""),
      status: editSupplierStatus ? "active" : "inactive",
    };

    try {
      await updateSupplier(editingSupplierId, payload);
      toast.success(` ${window.translate("Supplier updated successfully")}`);
      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      toast.error(`❌ ${window.translate("Failed to update supplier")}`);
    }
  };

  return (
    <div>
      <>
        {/* Add Supplier */}
        <div className="modal fade" id="add-supplier">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div className="page-title">
                  <h4>{window.translate("Add Supplier")}</h4>
                </div>
                <button
                  id="close-add-stock-btn"
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => {
                    setSupplierImagePreview("");
                    setSupplierImageKey("");
                  }}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <form onSubmit={handleAddSupplier}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="new-employee-field">
                        <div className="profile-pic-upload mb-2 gap-2">
                          <div className="profile-pic position-relative text-center p-2">
                            {supplierImagePreview ? (
                              <div className="image-preview-container position-relative d-inline-block">
                                <img
                                  src={supplierImagePreview}
                                  alt="Preview"
                                  style={{
                                    width: "100px",
                                    height: "100px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    border: "1px solid #ccc",
                                  }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger position-absolute"
                                  style={{
                                    top: "-8px",
                                    right: "-8px",
                                    borderRadius: "50%",
                                    padding: "2px 6px",
                                    lineHeight: "1",
                                    fontSize: "12px",
                                  }}
                                  onClick={() => {
                                    setSupplierImagePreview("");
                                    setSupplierImageKey("");
                                  }}
                                >
                                  &times;
                                </button>
                              </div>
                            ) : (
                              <span>
                                <i data-feather="plus-circle" className="plus-down-add" />{" "}
                                {window.translate("addImage") || "Add Image"}
                              </span>
                            )}
                          </div>

                          <div className="mb-0">
                            <div className="image-upload mb-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleAddImageChange}
                              />
                              <div className="image-uploads">
                                <h4>{window.translate("uploadImage")}</h4>
                              </div>
                            </div>
                            <p className="text-muted" style={{ fontSize: "12px" }}>
                              JPEG, PNG up to 2 MB
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Add fields */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("First Name")} (EN) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierFirstNameEN}
                          onChange={(e) => setSupplierFirstNameEN(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("First Name")} (AR) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierFirstNameAR}
                          onChange={(e) => setSupplierFirstNameAR(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Last Name")} (EN) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierLastNameEN}
                          onChange={(e) => setSupplierLastNameEN(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Last Name")} (AR) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierLastNameAR}
                          onChange={(e) => setSupplierLastNameAR(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Email")} <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={supplierEmail}
                          onChange={(e) => setSupplierEmail(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Phone")} <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierPhone}
                          onChange={(e) => setSupplierPhone(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Address")} (EN) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierAddressEN}
                          onChange={(e) => setSupplierAddressEN(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Address")} (AR) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierAddressAR}
                          onChange={(e) => setSupplierAddressAR(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-10 col-10">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("City")} (EN) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierCityEN}
                          onChange={(e) => setSupplierCityEN(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-10 col-10">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("City")} (AR) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierCityAR}
                          onChange={(e) => setSupplierCityAR(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-10 col-10">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("State")} (EN) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierStateEN}
                          onChange={(e) => setSupplierStateEN(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-10 col-10">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("State")} (AR) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierStateAR}
                          onChange={(e) => setSupplierStateAR(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-10 col-10">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Country")} (EN) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierCountryEN}
                          onChange={(e) => setSupplierCountryEN(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-10 col-10">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Country")} (AR) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierCountryAR}
                          onChange={(e) => setSupplierCountryAR(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Postal Code")} <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={supplierPostalCode}
                          onChange={(e) => setSupplierPostalCode(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-0">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <span className="status-label">
                            {window.translate("status")}
                          </span>
                          <input
                            type="checkbox"
                            id="users5"
                            className="check"
                            checked={supplierStatus}
                            onChange={(e) => setSupplierStatus(e.target.checked)}
                          />
                          <label htmlFor="users5" className="checktoggle mb-0" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                  >
                    {window.translate("Add Supplier")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Add Supplier */}

        {/* Edit Supplier */}
        <div className="modal fade" id="edit-supplier">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("Edit Supplier")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <form onSubmit={handleEditSupplier}>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="new-employee-field">
                          <div className="profile-pic-upload mb- gap-2">
                            <div className="profile-pic position-relative text-center p-2">
                              {editSupplierImagePreview ? (
                                <div className="image-preview-container position-relative d-inline-block">
                                  <img
                                    src={editSupplierImagePreview}
                                    alt="Preview"
                                    style={{
                                      width: "100px",
                                      height: "100px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                      border: "1px solid #ccc",
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger position-absolute"
                                    style={{
                                      top: "-8px",
                                      right: "-8px",
                                      borderRadius: "50%",
                                      padding: "2px 6px",
                                      lineHeight: "1",
                                      fontSize: "12px",
                                    }}
                                    onClick={() => {
                                      setEditSupplierImagePreview("");
                                      setEditSupplierImageKey("");
                                    }}
                                  >
                                    &times;
                                  </button>
                                </div>
                              ) : (
                                <span>
                                  <i data-feather="plus-circle" className="plus-down-add" />{" "}
                                  {window.translate("addImage") || "Add Image"}
                                </span>
                              )}
                            </div>

                            <div className="mb-0">
                              <div className="image-upload mb-2">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleEditImageChange}
                                />
                                <div className="image-uploads">
                                  <h4>{window.translate("uploadImage")}</h4>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Edit fields */}
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("First Name")} (EN) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierFirstNameEN}
                            onChange={(e) => setEditSupplierFirstNameEN(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("First Name")} (AR) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierFirstNameAR}
                            onChange={(e) => setEditSupplierFirstNameAR(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Last Name")} (EN) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierLastNameEN}
                            onChange={(e) => setEditSupplierLastNameEN(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Last Name")} (AR) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierLastNameAR}
                            onChange={(e) => setEditSupplierLastNameAR(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Email")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            value={editSupplierEmail}
                            onChange={(e) => setEditSupplierEmail(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Phone")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierPhone}
                            onChange={(e) => setEditSupplierPhone(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Address")} (EN) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierAddressEN}
                            onChange={(e) => setEditSupplierAddressEN(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Address")} (AR) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierAddressAR}
                            onChange={(e) => setEditSupplierAddressAR(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6 col-sm-10 col-10">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("City")} (EN) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierCityEN}
                            onChange={(e) => setEditSupplierCityEN(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6 col-sm-10 col-10">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("City")} (AR) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierCityAR}
                            onChange={(e) => setEditSupplierCityAR(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6 col-sm-10 col-10">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("State")} (EN) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierStateEN}
                            onChange={(e) => setEditSupplierStateEN(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6 col-sm-10 col-10">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("State")} (AR) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierStateAR}
                            onChange={(e) => setEditSupplierStateAR(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6 col-sm-10 col-10">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Country")} (EN) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierCountryEN}
                            onChange={(e) => setEditSupplierCountryEN(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6 col-sm-10 col-10">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Country")} (AR) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierCountryAR}
                            onChange={(e) => setEditSupplierCountryAR(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Postal Code")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editSupplierPostalCode}
                            onChange={(e) => setEditSupplierPostalCode(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-0">
                          <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                            <span className="status-label">
                              {window.translate("status")}
                            </span>
                            <input
                              type="checkbox"
                              id="users6"
                              className="check"
                              checked={editSupplierStatus}
                              onChange={(e) => setEditSupplierStatus(e.target.checked)}
                            />
                            <label htmlFor="users6" className="checktoggle mb-0" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                      data-bs-dismiss="modal"
                    >
                      {window.translate("cancel")}
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    >
                      {window.translate("Edit Supplier")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {/* /Edit Supplier */}

        {/* Delete Modal (unchanged) */}
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content p-5">
              <div className="modal-body text-center p-0">
                <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                  <i className="ti ti-trash fs-24 text-danger" />
                </span>
                <h4 className="fs-20 text-gray-9 fw-bold mb-2 mt-1">
                  {window.translate("Delete")}
                </h4>
                <p className="text-gray-6 mb-0 fs-16">
                  {window.translate("Are you sure you want to delete supplier")}
                </p>
                <div className="d-flex justify-content-center mt-3">
                  <Link
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("Cancel")}
                  </Link>
                  <Link
                    to="#"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    data-bs-dismiss="modal"
                    onClick={onDeleteSupplier}
                  >
                    {window.translate("Yes Delete")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Delete Modal */}
      </>
    </div>
  );
};

export default SupplierModal;

SupplierModal.propTypes = {
  selectedSupplier: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.shape({ en: PropTypes.string, ar: PropTypes.string }),
    lastName: PropTypes.shape({ en: PropTypes.string, ar: PropTypes.string }),
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.shape({ en: PropTypes.string, ar: PropTypes.string }),
    city: PropTypes.shape({ en: PropTypes.string, ar: PropTypes.string }),
    state: PropTypes.shape({ en: PropTypes.string, ar: PropTypes.string }),
    country: PropTypes.shape({ en: PropTypes.string, ar: PropTypes.string }),
    postalCode: PropTypes.string,
    image: PropTypes.string,
    status: PropTypes.string,
  }),
  onDeleteSupplier: PropTypes.func,
};
